import { useEffect, useMemo, useState } from 'react';
import resolveApiBase from '../tradingCockpit/apiBase';

const STRATEGY_ID = 'broker_login';
const API_BASE = resolveApiBase();
const API_KEY = process.env.REACT_APP_API_KEY;
// broker_login runs once a day (systemd timer at 07:55 IST); no need to poll
// aggressively — 30s keeps the card fresh enough for operator visibility.
const POLL_INTERVAL_MS = 30000;

const resolveUrl = (path) => {
    if (!API_BASE) {
        return path;
    }
    const normalisedBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${normalisedBase}${path.startsWith('/') ? path : `/${path}`}`;
};

const toEpochMs = (value) => {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return date.getTime();
};

const deriveDigest = (payload) => {
    if (!payload) {
        return null;
    }
    const accounts = Array.isArray(payload.accounts) ? payload.accounts : [];
    const events = Array.isArray(payload.events) ? payload.events : [];

    // Each account's most-recent strategy status tells us ok vs failed for
    // today's run. Activity snapshots are per-account so `accounts.length`
    // equals the number of accounts that posted telemetry this run.
    const okCount = accounts.filter((account) => {
        const status = (account?.status || '').toLowerCase();
        return status === 'operational' || status === 'ok' || status === 'success';
    }).length;
    const failingAccounts = accounts
        .filter((account) => {
            const status = (account?.status || '').toLowerCase();
            return status === 'error' || status === 'failed';
        })
        .map((account) => account.name || account.accountId)
        .filter(Boolean);

    const totalCount = accounts.length;

    const lastRunAt =
        payload.summary?.lastHeartbeatAt ||
        accounts[0]?.strategies?.[0]?.summary?.lastHeartbeatAt ||
        events[0]?.occurredAt ||
        null;

    const lastRunMessage =
        payload.summary?.lastMessage ||
        events[0]?.message ||
        null;

    let overallStatus = 'initialising';
    if (totalCount > 0) {
        if (failingAccounts.length > 0 && okCount === 0) {
            overallStatus = 'error';
        } else if (failingAccounts.length > 0) {
            overallStatus = 'warning';
        } else if (okCount > 0) {
            overallStatus = 'operational';
        }
    }

    return {
        overallStatus,
        okCount,
        totalCount,
        failingAccounts,
        lastRunAt,
        lastRunMessage,
    };
};

export function useBrokerLoginMonitor() {
    const [state, setState] = useState({
        digest: null,
        loading: true,
        error: null,
    });

    useEffect(() => {
        let mounted = true;

        const fetchState = async () => {
            try {
                const response = await fetch(resolveUrl(`/api/strategies/${STRATEGY_ID}/state`), {
                    headers: {
                        Accept: 'application/json',
                        ...(API_KEY ? { 'x-api-key': API_KEY } : {}),
                    },
                });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const payload = await response.json();
                if (!mounted) {
                    return;
                }
                setState({
                    digest: deriveDigest(payload),
                    loading: false,
                    error: null,
                });
            } catch (error) {
                if (!mounted) {
                    return;
                }
                setState((current) => ({
                    digest: current.digest,
                    loading: false,
                    error,
                }));
            }
        };

        fetchState();
        const intervalId = window.setInterval(fetchState, POLL_INTERVAL_MS);

        return () => {
            mounted = false;
            window.clearInterval(intervalId);
        };
    }, []);

    const lastRunAgeMs = useMemo(() => {
        const ms = toEpochMs(state.digest?.lastRunAt);
        return ms == null ? null : Math.max(0, Date.now() - ms);
    }, [state.digest?.lastRunAt]);

    return {
        digest: state.digest,
        loading: state.loading,
        error: state.error,
        lastRunAgeMs,
    };
}
